import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LucideIcon } from "lucide-react";
import { FC, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";

interface SortableTabTriggerProps {
  id: string;
  value: string;
  label: string;
  icon: LucideIcon;
  isDragging?: boolean;
}

export const SortableTabTrigger: FC<SortableTabTriggerProps> = ({
  id,
  value,
  label,
  icon: Icon,
  isDragging = false,
}) => {
  const isDraggingRef = useRef(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging: isSortableDragging,
  } = useSortable({
    id,
    data: {
      type: "tab",
      tabId: id,
    },
  });

  const style = transform
    ? {
        transform: CSS.Transform.toString(transform),
        touchAction: "none",
      }
    : undefined;

  const handleMouseDown = (event: React.MouseEvent) => {
    isDraggingRef.current = false;
    if (listeners?.onMouseDown) {
      listeners.onMouseDown(event);
    }
  };

  const handleDragStart = () => {
    isDraggingRef.current = true;
  };

  const handleDragEnd = () => {
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 0);
  };

  const handleClick = (event: React.MouseEvent) => {
    if (isDraggingRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  };

  const handlePointerDown = (event: React.PointerEvent) => {
    isDraggingRef.current = false;
    if (listeners?.onPointerDown) {
      listeners.onPointerDown(event);
    }
  };

  const handlePointerUp = (event: React.PointerEvent) => {
    if (isDraggingRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  };

  return (
    <TabsTrigger
      ref={setNodeRef}
      style={style}
      value={value}
      className={cn(
        "group relative flex items-center gap-2 cursor-grab active:cursor-grabbing transition-none select-none",
        (isDragging || isSortableDragging) && "z-50"
      )}
      {...attributes}
      {...listeners}
      onMouseDown={handleMouseDown}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (isDraggingRef.current && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <Icon className="w-4 h-4 flex-shrink-0 md:hidden" />
      <span className="hidden md:inline">
        <FormattedMessage id={label} />
      </span>
    </TabsTrigger>
  );
};
